import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Filter, AlertCircle, Shield, Activity } from 'lucide-react';
import Header from '../../components/ui/Header';
import { fetchAuditLogs } from '../../services/auditLogService';
import { realtimeNotificationService } from '../../services/realtimeNotificationService';
import { supabase } from '../../lib/supabase';

import FilterSidebar from './components/FilterSidebar';
import { usePagination } from '../../hooks/usePagination';
import { cacheManager } from '../../utils/cacheManager';
import Pagination from '../../components/ui/Pagination';
import StatsWidget from './components/StatsWidget';




// Lazy load timeline component
const TimelineView = lazy(() => import('./components/TimelineView'));

export default function AuditLogDashboard() {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [error, setError] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Cache key
  const AUDIT_LOGS_CACHE_KEY = 'audit_logs_data';

  // Filter state
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    eventType: 'all',
    severity: '',
    userId: '',
    searchQuery: ''
  });

  // Real-time subscription setup
  useEffect(() => {
    const setupRealtimeSubscriptions = async () => {
      try {
        // Get user's company ID
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;

        const { data: employeeData } = await supabase
          ?.from('company_employees')
          ?.select('company_id')
          ?.eq('user_id', user?.id)
          ?.single();

        if (!employeeData?.company_id) return;

        const companyId = employeeData?.company_id;

        // Subscribe to audit logs
        realtimeNotificationService?.subscribeToAuditLogs(companyId, (payload) => {
          console.log('Real-time audit log update:', payload);
          
          if (payload?.eventType === 'INSERT' && payload?.new) {
            // Add new log to the beginning of the list
            setAuditLogs(prevLogs => [payload?.new, ...prevLogs]);
            setFilteredLogs(prevLogs => [payload?.new, ...prevLogs]);
            
            // Clear cache to force refresh on next load
            cacheManager?.remove(AUDIT_LOGS_CACHE_KEY);
          }
        });
      } catch (error) {
        console.error('Error setting up real-time subscriptions:', error);
      }
    };

    setupRealtimeSubscriptions();

    // Cleanup subscriptions on unmount
    return () => {
      realtimeNotificationService?.unsubscribeAll();
    };
  }, []);

  // Fetch data with caching
  useEffect(() => {
    const loadAuditLogs = async () => {
      try {
        setIsLoadingData(true);

        // Check cache first
        const cachedLogs = cacheManager?.get(AUDIT_LOGS_CACHE_KEY);
        if (cachedLogs) {
          setAuditLogs(cachedLogs);
          setFilteredLogs(cachedLogs);
          setIsLoadingData(false);
          return;
        }

        // Fetch fresh data
        const logsData = await fetchAuditLogs(filters);
        
        // Cache the results
        cacheManager?.set(AUDIT_LOGS_CACHE_KEY, logsData || [], 5, 15); // Shorter cache for audit logs

        setAuditLogs(logsData || []);
        setFilteredLogs(logsData || []);
      } catch (error) {
        console.error('Error loading audit logs:', error);
        setError('Failed to load audit logs');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadAuditLogs();
  }, []);

  // Pagination for audit logs
  const {
    currentData: paginatedLogs,
    currentPage,
    totalPages,
    goToPage,
    getPageRange,
    startIndex,
    endIndex,
    totalItems,
    resetPagination
  } = usePagination(filteredLogs, 25);

  // Filter handler with pagination reset
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    resetPagination();
    
    const filtered = auditLogs?.filter(log => {
      const matchesAction = !newFilters?.action || log?.action === newFilters?.action;
      const matchesUser = !newFilters?.user || log?.user_id === newFilters?.user;
      const matchesDateRange = (!newFilters?.startDate || new Date(log?.created_at) >= new Date(newFilters.startDate)) &&
                               (!newFilters?.endDate || new Date(log?.created_at) <= new Date(newFilters.endDate));
      return matchesAction && matchesUser && matchesDateRange;
    });
    
    setFilteredLogs(filtered);
  };

  // Loading spinner
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Audit Log Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Monitor system activity and changes</p>
        </div>

        {/* Stats Widgets - Responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Stat widgets with mobile-friendly sizing */}
          <StatsWidget 
            title="Total Events"
            value={auditLogs?.length}
            icon={Activity}
          />
          <StatsWidget
            title="Admin Actions"
            value={stats?.byEventType?.admin_actions || 0}
            icon={Shield}
            color="green"
          />
          <StatsWidget
            title="2FA Events"
            value={stats?.byEventType?.['2fa_events'] || 0}
            icon={AlertCircle}
            color="purple"
          />
          <StatsWidget
            title="Critical Events"
            value={stats?.bySeverity?.critical || 0}
            icon={AlertCircle}
            color="red"
          />
        </div>

        {/* Main Content - Sidebar collapses on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="lg:col-span-1">
            {/* Collapsible filter sidebar on mobile */}
            <FilterSidebar 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </div>
          <div className="lg:col-span-3">
            {/* Timeline with responsive spacing */}
            <Suspense fallback={<LoadingSpinner />}>
              <TimelineView logs={paginatedLogs} />
            </Suspense>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              pageRange={getPageRange()}
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={totalItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
}